import React, { useState, useEffect } from "react";
import api from "../../../api/axiosConfig";
import "../../../css/AddStationSubModal.css";
import { showToast } from "../../Shared/Toaster";

// --- Interfaces for API & Component Data ---
interface ApiStation {
  _id: string;
  name: string;
  role?: any;
  roleName?: string;
  subrole?: any;
  subroleName?: string;
}

interface UserWithRoles {
  _id: string;
  displayName: string;
  entraObjectId: string;
  email?: string;
  roleAssignments?: any[];
}

// Data passed back to the parent component
export interface StationStaffData {
  stationId: string;
  stationName: string;
  staffId: string;
  staffName: string;
  oid: string;
  email?: string;
}

// Represents a fully assigned station row in the UI
export interface AssignedStation extends StationStaffData {
  id: string; // A unique client-side ID for React keys
}

// --- Component Props ---
interface AddStationSubModalProps {
  onClose: () => void;
  onSave: (data: AssignedStation) => void;
  editData: AssignedStation | null; // Data for editing, null for adding
  centerId: string;
  centerName: string;
}

// Helper to check if a user's assigned roles/subroles match a station's role/subrole
const userMatchesStation = (
  user: UserWithRoles,
  station?: ApiStation,
): boolean => {
  if (!station) return true;
  if (!user.roleAssignments || !Array.isArray(user.roleAssignments))
    return false;

  const stationRoleId =
    typeof station.role === "object" ? station.role?._id : station.role;
  const stationRoleName = (
    station.roleName ||
    (typeof station.role === "object" ? station.role?.name : "") ||
    ""
  )
    .trim()
    .toLowerCase();
  const stationName = (station.name || "").trim().toLowerCase();

  const stationSubroleId =
    typeof station.subrole === "object"
      ? station.subrole?._id
      : station.subrole;
  const stationSubroleName = (station.subroleName || "").trim().toLowerCase();

  return user.roleAssignments.some((assignment: any) => {
    const roleId =
      typeof assignment.roleId === "object"
        ? assignment.roleId?._id
        : assignment.roleId;
    const roleName = (
      typeof assignment.roleId === "object"
        ? assignment.roleId?.name || assignment.roleId?.key
        : assignment.roleName || ""
    )
      .trim()
      .toLowerCase();

    // 1. Direct Role ID match
    if (stationRoleId && roleId && stationRoleId === roleId) {
      return true;
    }

    // 2. Role Name or Key match (case-insensitive)
    if (stationRoleName && roleName && stationRoleName === roleName) {
      return true;
    }

    // 3. Station Name matching Role Name
    if (stationName && roleName && stationName === roleName) {
      return true;
    }

    // 4. Subrole match if present
    if (stationSubroleId || stationSubroleName) {
      const subroleId =
        typeof assignment.subroleId === "object"
          ? assignment.subroleId?._id
          : assignment.subroleId;
      const subroleName = (
        typeof assignment.subroleId === "object"
          ? assignment.subroleId?.name
          : assignment.subroleName || ""
      )
        .trim()
        .toLowerCase();

      if (stationSubroleId && subroleId && stationSubroleId === subroleId) {
        return true;
      }
      if (
        stationSubroleName &&
        subroleName &&
        stationSubroleName === subroleName
      ) {
        return true;
      }
    }

    return false;
  });
};

const AddStationSubModal: React.FC<AddStationSubModalProps> = ({
  onClose,
  onSave,
  editData,
  centerId,
  centerName,
}) => {
  const [stations, setStations] = useState<ApiStation[]>([]);
  const [staff, setStaff] = useState<UserWithRoles[]>([]);

  const [selectedStationId, setSelectedStationId] = useState(
    editData?.stationId || "",
  );
  const [selectedStaffId, setSelectedStaffId] = useState(
    editData?.staffId || "",
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!editData;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) throw new Error("Authentication token not found.");

        const stationPromise = api.center_api.get<{ data: ApiStation[] }>(
          "/api/v1/stations/getstations",
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const staffPromise = api.auth_api.get<UserWithRoles[]>(
          "/users/with-roles/full",
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const [stationResponse, staffResponse] = await Promise.all([
          stationPromise,
          staffPromise,
        ]);

        setStations(stationResponse.data.data || []);

        // Filter out users who have no role assignments
        const filteredStaff = (staffResponse.data || []).filter(
          (user: any) =>
            user.roleAssignments && user.roleAssignments.length > 0,
        );
        setStaff(filteredStaff);
      } catch (err: any) {
        console.error("Failed to fetch data for modal:", err);
        setError(err.message || "Could not load required data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleStationChange = (stationId: string) => {
    setSelectedStationId(stationId);
    const targetStation = stations.find((s) => s._id === stationId);
    const validStaff = staff.filter((user) =>
      userMatchesStation(user, targetStation),
    );

    // If currently selected staff doesn't match the newly selected station, reset staff selection
    if (!validStaff.some((u) => u._id === selectedStaffId)) {
      setSelectedStaffId("");
    }
  };

  // Get currently selected station and filter staff accordingly
  const selectedStation = stations.find((s) => s._id === selectedStationId);
  const availableStaff = selectedStation
    ? staff.filter((user) => userMatchesStation(user, selectedStation))
    : staff;

  // Handles both creating and updating an assignment
  const handleSaveClick = () => {
    if (!selectedStationId || !selectedStaffId) {
      showToast("Please select both a station and a staff member.", "error");
      return;
    }

    const currentStation = stations.find((s) => s._id === selectedStationId);
    const currentStaff = staff.find((s) => s._id === selectedStaffId);

    if (!currentStation || !currentStaff) {
      showToast("Selected station or staff not found.", "error");
      return;
    }

    // On success, pass the complete data back to the parent to update the UI
    onSave({
      id: editData?.id || Date.now().toString(),
      stationId: currentStation._id,
      stationName: currentStation.name,
      staffId: currentStaff._id,
      staffName: currentStaff.displayName,
      oid: currentStaff.entraObjectId,
      email: currentStaff.email,
    });

    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content-sub" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-header-sub">
          {isEditMode ? "Edit Station & Staff" : "Add Station & Staff"}
        </h2>

        {loading && <p>Loading options...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}

        {!loading && !error && (
          <>
            <div className="form-group">
              <label>Station Name</label>
              <select
                value={selectedStationId}
                onChange={(e) => handleStationChange(e.target.value)}
              >
                <option value="" disabled>
                  Select Station
                </option>
                {stations.map((station) => (
                  <option key={station._id} value={station._id}>
                    {station.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Staff Assignment</label>
              <select
                value={selectedStaffId}
                onChange={(e) => setSelectedStaffId(e.target.value)}
              >
                <option value="" disabled>
                  {availableStaff.length === 0
                    ? "No staff found for this station role"
                    : "Select Staff"}
                </option>
                {availableStaff.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions-sub" style={{ paddingRight: "10px" }}>
              <button
                onClick={onClose}
                className="btn-secondary"
                style={{ padding: "8px 16px", minWidth: "80px", flexShrink: 0 }}
              >
                Close
              </button>
              <button
                onClick={handleSaveClick}
                className="btn-primary"
                style={{ padding: "8px 16px", minWidth: "80px", flexShrink: 0 }}
              >
                {isEditMode ? "Update" : "Add"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddStationSubModal;
